// @/services/app/store/base.js
import { cacheManager } from '@/services/app/CacheManager';

/**
 * BaseStore
 * 
 * Core application state management with sophisticated features:
 * - Comprehensive application state structure and management
 * - Multi-layer caching strategy with sessionStorage integration
 * - Real-time API health monitoring with fallback mechanisms
 * - Intelligent project ID resolution with multiple fallback sources
 * - Session tracking and optimization for fast-path initialization
 * - Robust error handling with context awareness
 * - State subscription system for reactive components
 * - Memory-efficient state persistence with partial data strategies
 * - Resilient state restoration with cross-tab awareness
 */
export class BaseStore {
  /**
   * Initialize the base store with comprehensive state structure
   */
  constructor() {
    // Initialize core state structure
    this.state = {
      initialized: false,
      loading: {
        // Value lists (static data)
        countries: false,
        hotspotSectors: false,
        scope3Sectors: false,
        activities: false,
        prefills: false,
        // carbon accounting
        emissionResults: false,
        activityData: false,
        spendVolume: false,
        spendDistribution: false,
        supplierInput: false,
        // self assessment
        companyQuestions: false,
        businessSegments: false,
        customerRequests: false,
        // supply chain management
        supplychainSuppliers: false,
        // user management
        users: false,
        // company profile
        companyProfile: false
      },
      errors: {},
      data: {
        // Value lists (static data)
        countries: [],
        hotspotSectors: [],
        scope3Sectors: [],
        activities: [],
        prefills: [],
        // carbon accounting
        emissionResults: {
          byYear: {},
          meta: { lastSync: null }
        },
        activityData: {
          byYear: {},
          meta: { lastSync: null }
        },
        spendVolume: {
          byYear: {},
          meta: { lastSync: null }
        },
        spendDistribution: {
          byYear: {},
          meta: { lastSync: null }
        },
        supplierInput: {
          byYear: {},
          meta: { lastSync: null }
        },
        // self assessment
        companyQuestions: {
          byYear: {},
          meta: { lastSync: null }
        },
        businessSegments: {
          bySector: {},
          meta: { lastSync: null }
        },
        customerRequests: {
          requests: [],
          meta: { lastSync: null }
        },
        // supply chain management
        supplychainSuppliers: {
          list: [],
          meta: { lastSync: null }
        },
        // user management
        users: {
          list: [],
          mainUserEmail: null,
          meta: { lastSync: null }
        },
        // company profile
        companyProfile: null,
        parsedProfile: null
      },
      // API health status
      apiHealth: {
        healthy: null,
        lastCheck: null,
        error: null
      },
      // Real-time status tracking
      syncStatus: null,
      calculationStatus: null
    };

    this.subscribers = new Map();

    // Session tracking for reload optimization
    this.sessionInfo = {
      initialized: false,
      lastFullInit: null,
      isReload: false
    };

    // Common error collection
    this.operationErrors = new Map();
  }

  /**
   * Initialize base functionality - to be called by child classes
   * @protected
   * @returns {Promise<Object>} Initialization result with fastPath and restored flags
   */
  async initializeBase() {
    // Detect if this is a reload and mark session as active
    if (typeof window !== 'undefined') {
      const { isPageReload, markSessionActive } = await import('@/utils/session-utils');
      this.sessionInfo.isReload = isPageReload();
      markSessionActive();
    }

    // Try fast-path initialization
    // NOTE: canUseFastInit() validates auth token, projectId match, and app state
    // The isPageReload check was removed because canUseFastInit() already ensures
    // cached data is valid - this enables fast path for new tabs with valid cache
    if (this.canUseFastInit()) {
      const restored = await this.restoreState();
      if (restored) {
        this.state.initialized = true;
        return { fastPath: true, restored: true };
      }
    }

    // Clean up legacy storage
    this.cleanupLocalStorage();

    return { fastPath: false, restored: false };
  }

  /**
   * Check API health with intelligent fallback mechanisms
   * Uses multiple endpoints to determine healthiness with timeout protection
   * 
   * @returns {Promise<boolean>} Health status - true if any endpoint is healthy
   */
  async checkApiHealth() {
    // Skip if checked recently
    const now = Date.now();
    if (this.state.apiHealth.lastCheck &&
      now - this.state.apiHealth.lastCheck < 60000) { // 1 minute
      return this.state.apiHealth.healthy;
    }

    try {
      // Use reliable endpoints that should always exist
      const fallbackEndpoints = [
        '/api',
        '/api/customer-requests',
        '/api/company-profile'
      ];

      for (const endpoint of fallbackEndpoints) {
        try {
          const response = await fetch(
            `${process.env.GATSBY_STRAPI_URL}${endpoint}`,
            {
              method: 'HEAD',
              headers: {
                'Content-Type': 'application/json'
              },
              signal: AbortSignal.timeout(3000)
            }
          );

          if (response.status < 500) {
            // Update health status
            this.state.apiHealth = {
              healthy: true,
              lastCheck: now
            };
            return true;
          }
        } catch (endpointError) {
          // Try next endpoint
          continue;
        }
      }

      // FALLBACK: In development, consider it healthy
      if (process.env.NODE_ENV === 'development') {
        this.state.apiHealth = {
          healthy: true,
          lastCheck: now,
          devBypass: true
        };
        return true;
      }

      this.state.apiHealth = {
        healthy: false,
        lastCheck: now
      };
      return false;
    } catch (error) {
      this.state.apiHealth = {
        healthy: false,
        lastCheck: now,
        error
      };

      // FALLBACK: In development, consider it healthy despite error
      if (process.env.NODE_ENV === 'development') {
        this.state.apiHealth.healthy = true;
        this.state.apiHealth.devBypass = true;
        return true;
      }

      return false;
    }
  }

  /**
   * Get current project ID from auth service with multiple fallback mechanisms
   * @returns {string|null} Project ID or null if not authenticated
   */
  getProjectId() {
    // Import auth service dynamically to avoid circular dependencies
    const { authService } = require('@/services/auth');

    try {
      // Try getting project ID from auth service first (most reliable source)
      const authState = authService.getAuthState();
      if (authState?.authenticated && authState.user?.projectId) {
        return authState.user.projectId;
      }

      // Get projectId from keycloak directly as fallback
      if (authService.keycloak?.idTokenParsed?.projectId) {
        return authService.keycloak.idTokenParsed.projectId;
      }

      // Use CacheManager to check all cached sources
      const projectId = cacheManager.getProjectId();
      if (projectId) {
        return projectId;
      }

      return null;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error in getProjectId:', error);
      }
      return null;
    }
  }

  /**
   * Save state to session storage with size optimization
   * @returns {Promise<boolean>} Success status of the save operation
   */
  async saveState() {
    try {
      // Get current project ID
      const projectId = this.getProjectId();

      // Import auth service dynamically to avoid circular dependencies
      const { authService } = require('@/services/auth');
      const authState = authService.getAuthState();

      // ============================================================
      // FIX: Exclude large data modules from sessionStorage cache
      // sessionStorage has ~5-10MB limit, but supplychainSuppliers with
      // 1000+ entries (each with AssessmentAnswersJSON) can be 50MB+
      // These modules will be reloaded from Strapi on page refresh
      // ============================================================
      const EXCLUDED_FROM_CACHE = [
        'supplychainSuppliers'  // Can be 50MB+ for large accounts
        // NOTE: 'users' was removed - it's small (just names/emails/roles)
        // and is needed for sidebar avatar display on fast-path reload
      ];

      // Create a filtered copy of data without large modules
      const dataToCache = {};
      for (const [key, value] of Object.entries(this.state.data)) {
        if (!EXCLUDED_FROM_CACHE.includes(key)) {
          dataToCache[key] = value;
        }
      }

      // Use CacheManager to save state
      const success = cacheManager.saveAppState({
        initialized: this.state.initialized,
        projectId,
        data: dataToCache,
        sessionInfo: this.sessionInfo,
        authInfo: {
          authenticated: authState?.authenticated || false,
          projectId: projectId,
          email: authState?.user?.email || ''
          // Deliberately NO token here. The auth service is the only owner of
          // tokens (in-memory + keycloakTokenState/crossTabAuthInfo). A copy
          // in the appState snapshot goes stale between saves, and restoring
          // it used to clobber a fresh token right after the silent re-login
          // on reload — the "reload doesn't fix it" half of the 2026-08-14
          // identification-loss bug.
        },
        apiHealth: this.state.apiHealth
      });

      return success;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to save state:', error);
      }
      return false;
    }
  }

  /**
   * Restore state from sessionStorage with validation
   * @returns {Promise<boolean>} Success status of the restore operation
   */
  async restoreState() {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('Attempting to restore application state');
      }

      // Use CacheManager to get app state
      const savedState = cacheManager.getAppState();
      if (!savedState) {
        return false;
      }

      // Validate data structure
      if (!savedState.data || typeof savedState.data !== 'object') {
        return false;
      }

      // Enhanced auth info restoration
      if ((savedState.authInfo && savedState.authInfo.projectId) || savedState.projectId) {
        const projectId = savedState.authInfo?.projectId || savedState.projectId;

        // Try to get the auth service
        const { authService } = require('@/services/auth');

        // Always update auth service state when restoring from cache
        if (authService && authService.state) {
          authService.state.authenticated = savedState.authInfo?.authenticated || true;

          // Ensure user profile exists
          if (!authService.state.userProfile) {
            authService.state.userProfile = {
              projectId: projectId,
              email: savedState.authInfo?.email || ''
            };
          } else if (!authService.state.userProfile.projectId) {
            authService.state.userProfile.projectId = projectId;
          }

          // NEVER write a token here. Older snapshots carried authInfo.token,
          // and pushing it back overwrote authService.state.token — the field
          // getStrapiToken() serves — with a stale token AFTER the reload's
          // silent SSO re-login had already stored a fresh one on the keycloak
          // instance. Every boot fetch then went out with a dead bearer (401,
          // "identification lost") until the next interval tick re-synced it.
        }
      }

      // Restore API health status
      if (savedState.apiHealth) {
        this.state.apiHealth = savedState.apiHealth;
      }

      // Restore value lists from cache
      this._restoreValueLists(savedState.data);

      // Restore all other data
      // NOTE: supplychainSuppliers and users are excluded from cache (too large)
      // They will be reloaded from Strapi after restore - see saveState() for details
      const dataKeys = [
        'customerRequests', 'businessSegments', 'companyProfile', 'parsedProfile',
        'users', 'companyQuestions', 'emissionResults', 'activityData',
        'spendVolume', 'spendDistribution', 'supplierInput', 'supplychainSuppliers'
      ];

      dataKeys.forEach(key => {
        if (savedState.data[key]) {
          try {
            this.state.data[key] = savedState.data[key];
          } catch (e) {
            // Continue with restoration
          }
        }
      });

      // Restore initialized state
      if (typeof savedState.initialized === 'boolean') {
        this.state.initialized = savedState.initialized;
      }

      // Restore session info
      if (savedState.sessionInfo) {
        this.sessionInfo = savedState.sessionInfo;
      }

      if (process.env.NODE_ENV === 'development') {
        console.log('Restored essential data from sessionStorage cache');
      }

      // ============================================================
      // CRITICAL FIX: Notify subscribers after state restoration
      // This ensures components that mounted BEFORE restoration
      // get updated with the restored data (cross-tab scenario)
      // ============================================================
      // Use setTimeout to ensure this runs after React's commit phase
      // so components have time to subscribe before notifications
      setTimeout(() => {
        // Notify all data module subscribers
        const dataModules = [
          'businessSegments',
          'customerRequests',
          'companyQuestions',
          'emissionResults',
          'activityData',
          'spendVolume',
          'spendDistribution',
          'supplierInput',
          'supplychainSuppliers',
          'users',
          'companyProfile'
        ];

        dataModules.forEach(module => {
          if (this.state.data[module]) {
            this.notifySubscribers(module, this.state.data[module]);
          }
        });

        // Also notify loading state subscribers
        this.notifySubscribers('loading');

        if (process.env.NODE_ENV === 'development') {
          console.log('[BaseStore] Notified subscribers after state restoration');
        }
      }, 0);

      return true;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('State restoration failed:', error);
      }
      return false;
    }
  }

  /**
   * Restore value lists from cache while maintaining GraphQL compatibility
   * @private
   * @param {Object} cachedData - Data from cache
   */
  _restoreValueLists(cachedData) {
    const valueLists = ['countries', 'hotspotSectors', 'scope3Sectors', 'activities', 'prefills'];

    valueLists.forEach(listName => {
      if (cachedData[listName]?.length > 0) {
        this.state.data[listName] = cachedData[listName].map(item => ({
          ...item,
          // Ensure ID is properly formatted for GraphQL
          id: item.id?.toString()
        }));
      }
    });
  }

  /**
   * Parse Snowflake data for company profiles
   * @param {Object} data - Raw profile data from Snowflake
   * @returns {Object|null} Parsed profile data with company and personal sections
   */
  parseSnowflakeData(data) {
    if (!data) return null;

    // Get data from different possible field names
    let companyData = data.CompanyData || data.COMPANYDATA;
    let personalData = data.PersonalData || data.PERSONALDATA;

    // Parse JSON strings if needed
    if (typeof companyData === 'string') {
      try {
        companyData = JSON.parse(companyData);
      } catch (e) {
        // Silent error
      }
    }

    if (typeof personalData === 'string') {
      try {
        personalData = JSON.parse(personalData);
      } catch (e) {
        // Silent error
      }
    }

    // Ensure we have proper objects
    companyData = companyData || {};
    personalData = personalData || {};

    // Add company email for UI (not in Snowflake schema)
    companyData.Email = personalData.Email || "";

    return { company: companyData, personal: personalData };
  }

  /**
   * Check if fast-path initialization can be used
   * FIXED: Now uses CacheManager for unified cache checking
   * @returns {boolean} True if fast-path available
   */
  canUseFastInit() {
    try {
      // For pages with invitation tokens, always use full init
      if (typeof window !== 'undefined') {
        const params = new URLSearchParams(window.location.search);
        if (params.get('token') || params.get('invitation') || params.get('invitationToken')) {
          return false;
        }

        // For pages being redirected from auth, use full init
        if (params.has('code') || params.has('state')) {
          return false;
        }
      }

      // Check if state is initialized in memory
      if (this.state.initialized) { 
        return true;
      }

      // Use CacheManager to check if we can use fast path
      return cacheManager.canUseFastPath();
    } catch (error) {
      return false;
    }
  }

  /**
   * Subscribe to state changes with callback registration
   * @param {string} type - Type of data to subscribe to
   * @param {Function} callback - Callback function to be notified of changes
   * @returns {Function} Unsubscribe function to remove the subscription
   */
  subscribe(type, callback) {
    if (!this.subscribers.has(type)) {
      this.subscribers.set(type, new Set());
    }
    this.subscribers.get(type).add(callback);
    return () => this.subscribers.get(type).delete(callback);
  }

  /**
   * Notify subscribers of state changes
   * @param {string} type - Type of data that changed
   * @param {*} data - Optional data to pass to subscribers
   */
  notifySubscribers(type, data = null) {
    const subscribers = this.subscribers.get(type);
    if (subscribers) {
      subscribers.forEach(callback => callback(data || this.state.data[type]));
    }
  }

  /**
   * Handle errors in store operations with contextual awareness
   * @param {string} context - Context where error occurred
   * @param {Error|null} error - Error object or null to clear error
   */
  handleError(context, error) {
    if (!error) {
      delete this.state.errors[context];
      this.operationErrors.delete(context);
    } else {
      const errorData = {
        message: error.message,
        timestamp: new Date().toISOString(),
        context
      };

      this.state.errors[context] = errorData;
      this.operationErrors.set(context, errorData);

      if (process.env.NODE_ENV === 'development') {
        console.error(`Error in ${context}:`, error);
      }
    }
    this.notifySubscribers('error');
  }

  /**
   * Clean up legacy localStorage cache items
   */
  cleanupLocalStorage() {
    if (typeof localStorage !== 'undefined') {
      try {
        const localStorageKeys = Object.keys(localStorage);
        let removedCount = 0;

        for (const key of localStorageKeys) {
          if (key === 'appState' || key.startsWith('appState_')) {
            localStorage.removeItem(key);
            removedCount++;
          }
        }

        if (removedCount > 0 && process.env.NODE_ENV === 'development') {
          console.log(`Cleaned up ${removedCount} legacy localStorage cache items`);
        }
      } catch (e) {
        // Ignore storage errors
      }
    }
  }

  /**
   * Clear all base state and cache
   * @protected
   * @returns {Promise<boolean>} Success status
   */
  async clearBaseState() {
    // Clear memory state
    this.state.data = {
      countries: [],
      hotspotSectors: [],
      scope3Sectors: [],
      activities: [],
      prefills: [],
      emissionResults: { byYear: {}, meta: { lastSync: null } },
      activityData: { byYear: {}, meta: { lastSync: null } },
      spendVolume: { byYear: {}, meta: { lastSync: null } },
      spendDistribution: { byYear: {}, meta: { lastSync: null } },
      supplierInput: { byYear: {}, meta: { lastSync: null } },
      companyQuestions: { byYear: {}, meta: { lastSync: null } },
      businessSegments: { bySector: {}, meta: { lastSync: null } },
      customerRequests: { requests: [], meta: { lastSync: null } },
      supplychainSuppliers: { list: [], meta: { lastSync: null } },
      users: { list: [], mainUserEmail: null, meta: { lastSync: null } },
      companyProfile: null,
      parsedProfile: null
    };

    // Clear errors
    this.state.errors = {};
    this.operationErrors.clear();

    // Use CacheManager to clear all cache
    cacheManager.clearAll();

    // Reset state flags
    this.state.initialized = false;
    this.sessionInfo = {
      initialized: false,
      lastFullInit: null,
      isReload: false
    };

    // Also clear any legacy localStorage items to clean up old cached data
    this.cleanupLocalStorage();

    if (process.env.NODE_ENV === 'development') {
      console.log('[BaseStore] Base state and cache cleared');
    }

    return true;
  }

  /**
   * Clear all application state and cache
   * To be overridden by child classes for additional cleanup
   * @returns {Promise<boolean>} Success status
   */
  async clearState() {
    return this.clearBaseState();
  }
}